"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Button from "../../../../components/Button";
import Checkbox from "../../../../components/Checkbox";
import styles from "../css/splitProfile.module.css";
import { getProfileByPatient } from "../../../../api/profile/create_profile";

export default function SplitProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const patientId = location.state?.patientId;
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);

  useEffect(() => {
    if (!patientId) {
      navigate("/customer/profiles/split-profiles");
      return;
    }

    let cancelled = false;

    async function loadProfile() {
      try {
        const data = await getProfileByPatient(patientId);
        if (!data || cancelled) return;

        const main = data.main || {};
        const mm = data.mainmember || {};
        const profile = data.profile || {};
        const bens = Array.isArray(data.beneficiaries) ? data.beneficiaries : [];

        const homeParts = String(main.homeaddress || "")
          .split(",")
          .map((s) => s.trim());

        const d = main.deliveryaddress;
        let delivery = { street: "", suburb: "", city: "", postal: "" };
        if (d && typeof d === "object" && !Array.isArray(d)) {
          const addr = d["Delivery Address 1"] || {};
          delivery = {
            street: addr.street || "",
            suburb: addr.suburb || "",
            city: addr.city || "",
            postal: addr.postalCode || addr.postal || "",
          };
        } else if (d) {
          const parts = String(d)
            .split(",")
            .map((s) => s.trim());
          delivery = {
            street: parts[0] || "",
            suburb: parts[1] || "",
            city: parts[2] || "",
            postal: parts[3] || "",
          };
        }

        const chronic = Array.isArray(main.chronicconditions)
          ? main.chronicconditions.join(", ")
          : main.chronicconditions || "";

        setProfileData({
          basePatientId: main.patientid,
          profileNumber: profile.profilenumber || "",
          fullName: `${main.firstname || ""} ${main.surname || ""}`.trim(),
          idNumber: main.idnumber || "",
          emailAddress: main.email || "",
          sex: main.sex || "",
          homeCellNumber: main.contactnumber || "",
          chronicConditions: chronic,
          medicalAidProvider: mm.medicalaidprovider || "",
          medicalAidNumber: mm.medicalaidnumber || "",
          doctor: `${mm.practitioner?.title || ""} ${mm.practitioner?.firstname || ""} ${mm.practitioner?.surname || ""}`.trim(),
          practiceNumber: mm.practitioner?.practicenumber || "",
          postalAddressLine1: homeParts[0] || "",
          postalAddressLine2: homeParts[1] || "",
          postalAddressLine3: homeParts[2] || "",
          postalAddressLine4: homeParts[3] || "",
          deliveryAddressLine1: delivery.street || "",
          deliveryAddressLine2: delivery.suburb || "",
          deliveryAddressLine3: delivery.city || "",
          deliveryAddressLine4: delivery.postal || "",
        });

        const mappedBens = bens.map((b) => {
          const chronic = Array.isArray(b.chronicconditions)
            ? b.chronicconditions.join(", ")
            : b.chronicconditions || "";

          return {
            id: b.patientid,
            name: `${b.firstname || ""} ${b.surname || ""}`.trim(),
            idNumber: b.idnumber || "",
            emailAddress: b.email || "",
            sex: b.sex || "",
            homeCellNumber: b.contactnumber || "",
            chronicConditions: chronic,
          };
        });
        setBeneficiaries(mappedBens);
      } catch (_) {
        navigate("/customer/profiles/split-profiles");
      }
    }

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [navigate, patientId]);

  const handleCheckboxChange = (id) => {
    setSelectedBeneficiaries((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSplit = () => {
    if (!profileData) return;

    if (!beneficiaries.length) {
      return;
    }

    if (selectedBeneficiaries.length === beneficiaries.length) {
      window.alert("Please unselect at least one beneficiary so there is someone to move to the new main member.");
      return;
    }

    navigate("/customer/profile/complete-split-profile", {
      state: {
        patientId,
        basePatientId: profileData.basePatientId,
        selectedStayIds: selectedBeneficiaries,
        profileData,
        beneficiaries,
      },
    });
  };

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />

        <div className={styles.content}>

          <div className={styles.formContainer}>
            <div className={styles.layout}>
              <div className={styles.leftColumn}>
                <h2 className={styles.mainTitle}>
                  Profile Number: {profileData?.profileNumber || ""}
                </h2>
                <div className={styles.field}>
                  <label>Name</label>
                  <span className={styles.boldName}>{profileData?.fullName || ""}</span>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>ID Number</label>
                    <input type="text" value={profileData?.idNumber || ""} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Email Address</label>
                    <input type="text" value={profileData?.emailAddress || ""} readOnly />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Sex</label>
                    <input type="text" value={profileData?.sex || ""} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Home/Cell Number</label>
                    <input type="text" value={profileData?.homeCellNumber || ""} readOnly />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Chronic Conditions</label>
                  <input type="text" value={profileData?.chronicConditions || ""} readOnly />
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Medical Aid Provider</label>
                    <input type="text" value={profileData?.medicalAidProvider || ""} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Medical Aid Number</label>
                    <input type="text" value={profileData?.medicalAidNumber || ""} readOnly />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Doctor</label>
                    <input type="text" value={profileData?.doctor || ""} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Practice Number</label>
                    <input type="text" value={profileData?.practiceNumber || ""} readOnly />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Select Beneficiaries to Stay:</label>
                  <div className={styles.checkboxWrapper}>
                    {beneficiaries.map((beneficiary) => {
                      const isChecked = selectedBeneficiaries.includes(beneficiary.id);
                      return (
                        <Checkbox
                          key={beneficiary.id}
                          id={`beneficiary-${beneficiary.id}`}
                          label={beneficiary.name}
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(beneficiary.id)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className={styles.rightColumn}>
                <div className={styles.addressGroup}>
                  <label>Postal Address</label>
                  <input type="text" value={profileData?.postalAddressLine1 || ""} readOnly />
                  <input type="text" value={profileData?.postalAddressLine2 || ""} readOnly />
                  <input type="text" value={profileData?.postalAddressLine3 || ""} readOnly />
                  <input type="text" value={profileData?.postalAddressLine4 || ""} readOnly />
                </div>
                <div className={styles.addressGroup}>
                  <label>Delivery Address</label>
                  <input type="text" value={profileData?.deliveryAddressLine1 || ""} readOnly />
                  <input type="text" value={profileData?.deliveryAddressLine2 || ""} readOnly />
                  <input type="text" value={profileData?.deliveryAddressLine3 || ""} readOnly />
                  <input type="text" value={profileData?.deliveryAddressLine4 || ""} readOnly />
                </div>
              </div>
            </div>

            <div className={styles.buttonContainer}>
              <Button variant="primary" onClick={handleSplit}>
                Split
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}